import { ButtonSearch } from '@/components/common/Button';
import { Checkbox } from '@/components/common/Checkbox';
import Combobox from '@/components/common/Combobox';
import { Form, FormControl, FormField, FormItem, FormLabel } from '@/components/common/Form';
import { Input } from '@/components/common/Input';
import { useStore } from '@/hooks/useStore';
import { ROLE_USER } from '@/types/enums';
import { monthsCombobox, yearsCombobox } from '@/utils/datetime';
import { observer } from 'mobx-react-lite';
import { UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface FilterProps {
    form: UseFormReturn<any>,
    onSearch: (searchParams?: any) => void
}

export default observer(function FilterForm({
    form,
    onSearch
}: FilterProps) {
    // hooks
    const { t } = useTranslation();

    // store
    const {
        accountStore: { accounts },
        locationStore: { locations },
        bikeStore: { bikeModelList }
    } = useStore();

    const years = yearsCombobox();

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSearch)}>
                <div className='grid gap-4 md:gap-6 grid-cols-1 xsm:grid-cols-2 xl:grid-cols-3'>
                    <FormField
                        control={form.control}
                        name='ownerId'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.owner')}
                                        value={field.value ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        items={
                                            (Array.isArray(accounts) && accounts?.length ?
                                                [
                                                    ...accounts
                                                        .filter(account => (
                                                            Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_EK &&
                                                            Number(account?.role?.id) !== ROLE_USER.RENTAL_BIKE_OWNER_RD
                                                        ))
                                                        .map(item => ({
                                                            label: item.fullname,
                                                            labelSearch: item.fullname + item?.userCode,
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                ] :
                                                []
                                            )
                                        }
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='locationId'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.location_name')}
                                        value={field.value ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        items={
                                            (Array.isArray(locations) && locations?.length ?
                                                [
                                                    ...locations
                                                        .filter(item => item.name)
                                                        .map(item => ({
                                                            label: item.name,
                                                            value: item.id.toString(),
                                                            code: item.id
                                                        }))
                                                ] :
                                                []
                                            )
                                        }
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='bikeModel'
                        render={({ field }) => (
                            <FormItem className='w-full'>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.model_name')}
                                        value={field.value}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        search={true}
                                        items={
                                            (Array.isArray(bikeModelList) && bikeModelList?.length > 0 ?
                                                [
                                                    ...bikeModelList.map(item => ({
                                                        label: item.bikeModel,
                                                        value: item.bikeModel,
                                                        code: item.bikeModel
                                                    }))
                                                ] :
                                                []
                                            )
                                        }
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <FormField
                        control={form.control}
                        name='year'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.year')}
                                        value={field.value.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        search={false}
                                        items={years.map(year => ({
                                            label: `${year}年`,
                                            value: year.toString()
                                        }))}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='month'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Combobox
                                        placeholder={t('field.month')}
                                        value={field.value.toString() ?? ''}
                                        onChange={(item) => {
                                            field.onChange(item?.value ?? '');
                                        }}
                                        search={false}
                                        items={monthsCombobox?.map(month => ({
                                            label: `${month}月`,
                                            value: month.toString()
                                        }))}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />
                    <FormField
                        control={form.control}
                        name='keyword'
                        render={({ field }) => (
                            <FormItem>
                                <FormControl>
                                    <Input
                                        type='text'
                                        placeholder={t('field.reservation_code_renter_name')}
                                        {...field}
                                    />
                                </FormControl>
                            </FormItem>
                        )}
                    />

                    <div className='col-span-full'>
                        <FormField
                            control={form.control}
                            name='isCancelBooking'
                            render={({ field }) => (
                                <FormItem className='flex items-center'>
                                    <FormControl>
                                        <Checkbox
                                            checked={field.value}
                                            onCheckedChange={field.onChange}
                                        />
                                    </FormControl>
                                    <FormLabel className='ml-1.5 mb-0 cursor-pointer font-normal'>
                                        {t('field.cancel_only_booking')}
                                    </FormLabel>
                                </FormItem>
                            )}
                        />
                    </div>

                    <div className='col-span-full flex justify-center'>
                        <ButtonSearch size='md' className='max-w-52'>
                            {t('button.search')}
                        </ButtonSearch>
                    </div>

                </div>
            </form>
        </Form>
    );
});
